import React, { useEffect, useMemo, useState } from 'react';
import {
  formatBeats,
  formatTimeSignature,
  getChordDisplayText,
  layoutBarSlots,
  removeSlotFromBar,
  setSlotBeats,
  splitSlotAt,
  updateSlotInRow,
  type ChordObjectModelV3,
  type SplitRestSide,
} from '@chordbaker/music-theory';
import { useTheme, Theme } from '../contexts/ThemeContext';
import type { BarState, RowState, SlotState, TimeSignature } from '../types';
import { generateUniqueId } from '../utils/idUtils';
import { slotName } from '../utils/beatsUtils';
import SectionEditorModalFrame from './SectionEditorModalFrame';
import ChordEditorModal from './ChordEditorModal';
import BarStrip from './BarStrip';
import PencilIcon from './icons/PencilIcon';
import TrashIcon from './icons/TrashIcon';
import UndoIcon from './icons/UndoIcon';

/** One state of the draft: the bar and the selection. */
interface DraftEntry {
  bar: BarState;
  selectedId: string;
}

const HINT = 'Click a chord to select it · + adds a slot beside it · drag a handle to resize';

const createStyles = (theme: Theme) => ({
  body: {
    padding: theme.spacing.xl,
    paddingTop: theme.spacing.xl,
    display: 'flex',
    flexDirection: 'column' as const,
  },
  hint: {
    margin: 0,
    marginTop: theme.spacing.xs,
    marginBottom: theme.spacing.xl,
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.textTertiary,
    textAlign: 'center' as const,
  },
  toolbar: {
    display: 'flex',
    gap: theme.spacing.sm,
  },
  toolButton: {
    flex: 1,
    minHeight: theme.sizes.hitTarget,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    padding: `0 ${theme.spacing.sm}px`,
    border: 'none',
    borderRadius: theme.radii.lg,
    backgroundColor: theme.colors.surfaceSecondary,
    color: theme.colors.text,
    fontSize: theme.typography.sizes.md,
    fontWeight: theme.typography.weights.medium,
    cursor: 'pointer',
    minWidth: 0,
  },
  toolText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  deleteButton: {
    flex: 'none',
    width: theme.sizes.hitTarget,
    color: theme.colors.error,
  },
  disabled: {
    opacity: theme.opacity.disabled,
    cursor: 'default',
  },
  undoButton: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    padding: `${theme.spacing.sm}px 0`,
    border: 'none',
    background: 'none',
    color: theme.colors.text,
    fontSize: theme.typography.sizes.md,
    cursor: 'pointer',
  },
});

export interface BarEditorModalProps {
  isOpen: boolean;
  row: RowState;
  /** The slot whose metronome button opened the editor (preselected). */
  slotId: string;
  meter: TimeSignature;
  onClose: () => void;
  /** The edited bar and the slot to leave active on the chart. */
  onApply: (barId: string, bar: BarState, focusSlotId: string) => void;
}

/**
 * The bar editor: the slot's whole bar as a strip of segments on a beat grid
 * (`BarStrip`: click to select, the selection's `+` chip to add an empty
 * slot before or after it, drag a handle to resize) over Chord / Delete.
 * Every edit runs a `barEditing.ts` operation on a one-bar draft row, so bars
 * stay solid by construction; Undo steps back through the draft, Cancel drops
 * it, Apply hands the bar back. Mirrors RN's BarEditorModal (the web has no
 * app-level undo, so the in-card Undo is the only one).
 */
const BarEditorModal: React.FC<BarEditorModalProps> = ({ isOpen, row, slotId, meter, onClose, onApply }) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  const barIndex = row.bars.findIndex(b => b.slots.some(s => s.id === slotId));
  const original = barIndex === -1 ? null : row.bars[barIndex];

  const [history, setHistory] = useState<DraftEntry[]>([]);
  const [selectedId, setSelectedId] = useState<string>(slotId);
  const [chordEditorOpen, setChordEditorOpen] = useState(false);

  // A fresh draft every time the editor opens
  useEffect(() => {
    if (isOpen && original) {
      setHistory([{ bar: original, selectedId: slotId }]);
      setSelectedId(slotId);
      setChordEditorOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, slotId]);

  // '' for a rest (getChordDisplayText would give its placeholder)
  const slotLabel = (slot: SlotState): string => (slot.chord || slot.customText ? getChordDisplayText(slot) : '');

  if (!original) return null;

  const entry: DraftEntry = history[history.length - 1] ?? { bar: original, selectedId: slotId };
  const draft = entry.bar;
  const draftRow: RowState = { ...row, bars: [draft] };
  const selected = draft.slots.find(s => s.id === selectedId) ?? draft.slots[0];
  const selectedIndex = draft.slots.indexOf(selected);

  const push = (bar: BarState, nextSelected: string) => {
    if (bar === draft) {
      setSelectedId(nextSelected);
      return;
    }
    setHistory(h => [...h, { bar, selectedId: nextSelected }]);
    setSelectedId(nextSelected);
  };

  const undo = () => {
    if (history.length < 2) return;
    const previous = history[history.length - 2];
    setHistory(h => h.slice(0, -1));
    setSelectedId(previous.selectedId);
  };

  const setContent = (chord: ChordObjectModelV3 | null, customText: string | null) =>
    push(updateSlotInRow(draftRow, selected.id, s => ({ ...s, chord, customText })).bars[0], selected.id);

  // The new rest takes the selection, so "Set chord" is the next click
  const split = (id: string, atUnits: number, restSide: SplitRestSide) => {
    const result = splitSlotAt(draftRow, id, atUnits, meter, generateUniqueId, restSide);
    if (result.insertedRestId) {
      push(result.row.bars[0], result.insertedRestId);
    }
  };

  const moveBoundary = (leftId: string, lengthUnits: number) => {
    const result = setSlotBeats(draftRow, leftId, lengthUnits, meter, generateUniqueId);
    push(result.row.bars[0], selected.id);
  };

  // Delete merges: the beats go to the slot before (after, when first), which takes the selection
  const neighbour = draft.slots[selectedIndex === 0 ? 1 : selectedIndex - 1];
  const remove = () => {
    if (!neighbour) return;
    push(removeSlotFromBar(draftRow, selected.id, meter).bars[0], neighbour.id);
  };

  const apply = () => {
    if (history.length < 2) {
      onClose();
      return;
    }
    onApply(original.id, draft, selected.id);
  };

  // The chord editor's result: a chord, custom text, or neither (a rest)
  const applyChordEdits = (data: { chord?: ChordObjectModelV3; customText?: string }) =>
    setContent(data.chord ?? null, data.chord ? null : data.customText ?? null);

  const selectedUnits = layoutBarSlots(draft, meter).find(l => l.slotId === selected.id)?.lengthUnits;

  const hasContent = !!selected.chord || !!selected.customText;
  const canUndo = history.length > 1;

  return (
    <SectionEditorModalFrame
      isOpen={isOpen}
      title="Edit bar"
      subtitle={`Bar ${barIndex + 1} of ${row.bars.length} · in ${formatTimeSignature(meter)}`}
      onClose={onClose}
      onApply={apply}
      onClear={onClose}
      clearLabel="Cancel"
      footerStart={
        <button
          type="button"
          onClick={undo}
          disabled={!canUndo}
          style={{ ...styles.undoButton, ...(canUndo ? {} : styles.disabled) }}
          data-testid="bar-editor-undo"
        >
          <UndoIcon size={theme.sizes.icon.md} color="currentColor" />
          Undo
        </button>
      }
    >
      <div style={styles.body}>
        <BarStrip
          bar={draft}
          meter={meter}
          selectedSlotId={selected.id}
          labelFor={slotLabel}
          onSelect={setSelectedId}
          onSplit={split}
          onMoveBoundary={moveBoundary}
        />
        <p style={styles.hint}>{HINT}</p>

        <div style={styles.toolbar}>
          <button type="button" onClick={() => setChordEditorOpen(true)} style={styles.toolButton} data-testid="bar-editor-chord">
            <PencilIcon size={theme.sizes.icon.sm} color="currentColor" />
            <span style={styles.toolText}>{hasContent ? `Chord: ${slotLabel(selected)}` : 'Set chord'}</span>
          </button>
          <button
            type="button"
            onClick={remove}
            disabled={!neighbour}
            aria-label={
              neighbour
                ? `Delete ${slotName(selected)}: its beats go to ${slotName(neighbour)}`
                : "The bar's only chord can't be deleted here"
            }
            style={{ ...styles.toolButton, ...styles.deleteButton, ...(neighbour ? {} : styles.disabled) }}
            data-testid="bar-editor-delete"
          >
            <TrashIcon size={theme.sizes.icon.md} color="currentColor" />
          </button>
        </div>
      </div>

      <ChordEditorModal
        isOpen={chordEditorOpen}
        onClose={() => setChordEditorOpen(false)}
        onApply={data => {
          applyChordEdits(data);
          setChordEditorOpen(false);
        }}
        onClear={() => {
          setContent(null, null);
          setChordEditorOpen(false);
        }}
        onMakeRest={() => {
          setContent(null, null);
          setChordEditorOpen(false);
        }}
        // The bar editor is already underneath: Beats keeps the edits the
        // user made and comes back to it
        onOpenBeats={edits => {
          if (edits) applyChordEdits(edits);
          setChordEditorOpen(false);
        }}
        beatsLabel={selectedUnits === undefined ? 'Beats' : `Beats · ${formatBeats(selectedUnits)}/${meter.numerator}`}
        initialChordData={{
          chord: selected.chord || undefined,
          customText: selected.customText ?? undefined,
        }}
      />
    </SectionEditorModalFrame>
  );
};

export default BarEditorModal;
